import classnames from 'classnames';
import { UnderlyingResults } from 'components/common-form/builder/form/node/field/BatchMultipleUnderlyingsField/MultipleUnderlyingsComponent/model';
import { HorizontalDivider } from 'components/shared/Divider';
import type { TagOption } from 'components/shared/Tags/model';
import * as menuList from 'components/shared/underlyings/selector/result/menu-list';
import * as UnderlyingResultsComponents from 'components/shared/underlyings/selector/result/no-option-message';
import * as option from 'components/shared/underlyings/selector/result/option';
import { useUnderlyingsBasket } from 'hooks/underlyings/use-underlyings-basket';
import { useUnderlyingsTags } from 'hooks/underlyings/use-underlyings-tags';
import type { Underlying } from 'models/underlying';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { UnderlyingSearch } from 'models/underlying/underlying-search';
import { FC, KeyboardEvent, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { displayUnderlyingCode } from '../../utils';
import { SmallButton } from './Buttons';
import { CustomizedPlaceholderFontLegacyTags } from './CustomizedLegacyTags';
import { WithInputGroupAppend } from './WithInputGroupAppend';

type Props = {
  id: string;
  isDisabled?: boolean;
  placeholder?: string;
  value?: readonly Underlying[];
  defaultTags?: ReadonlyArray<TagOption<Underlying>>;
  tabIndex?: number;
  className?: string;
  onChange(underlying: readonly Underlying[], autoCommit: boolean): void;
  defaultUnderlyingCodeType: UnderlyingCodeType;
  ResultsComponent: FC<UnderlyingResults>;
  isCellInactive?: boolean;
  onEditingKeyDown?: (e: KeyboardEvent) => void;
  isFocused?: boolean;
  lostInputBeforeEditing?: string;
  onTagsChanged: () => void;
  onInputChanged: () => void;
} & UnderlyingSearch;
const EMPTY_TAGS: readonly TagOption<Underlying>[] = [];
export function BatchMultipleUnderlyingsComponent({
  id,
  isDisabled = false,
  onChange,
  placeholder,
  value = [],
  tabIndex,
  defaultUnderlyingCodeType,
  className,
  ResultsComponent,
  isCellInactive = false,
  isFocused,
  lostInputBeforeEditing,
  onEditingKeyDown,
  getOptionsFromPastedLabels,
  search,
  onTagsChanged: onChanged,
  onInputChanged,
}: Props) {
  const {
    updateUnderlyings,
    tags,
    newTags,
    invalidTags,
    validTags,
    onTagsChanged,
    clearAllTags,
    onUnderlyingRemovedById,
  } = useUnderlyingsTags({
    underlyings: value,
    onChange: underlyings => onChange(underlyings, false),
    isMulti: true,
    codeType: defaultUnderlyingCodeType,
    underlyingsSyncMode: 'replace',
    removeInvalidTagsOnUnderlyingsChange: true,
  });
  const { selectedUnderlyings, selectedUnderlyingsIds } = useUnderlyingsBasket({ tags: tags, underlyings: value });
  const { locale } = useIntl();

  const handleConvertPastedLabelsToTags = useCallback(
    (pastedLabels: string[]) => {
      return getOptionsFromPastedLabels(pastedLabels, selectedUnderlyingsIds, defaultUnderlyingCodeType);
    },
    [getOptionsFromPastedLabels, selectedUnderlyingsIds, defaultUnderlyingCodeType],
  );

  const handleTagsChanged = useCallback(
    (newTags: readonly TagOption<Underlying>[]) => {
      onTagsChanged(newTags, false, 'add');
      onChanged();
    },
    [onTagsChanged, onChanged],
  );
  return (
    <div>
      <WithInputGroupAppend
        addOn={
          newTags.length > 0 ? (
            <SmallButton
              iconName="done"
              className="btn-light py-0"
              onClick={updateUnderlyings}
              e2e="underlying-commit-change"
              tooltipKey="form.Underlyings.validateTooltip"
            />
          ) : undefined
        }
      >
        <CustomizedPlaceholderFontLegacyTags
          locale={locale}
          isEditing={newTags.length > 0}
          components={{
            MenuList: menuList.MenuList,
            Option: option.Option,
            NoOptionsMessage: UnderlyingResultsComponents.NoOptionsMessage,
          }}
          convertPastedLabelsToTags={handleConvertPastedLabelsToTags}
          filterTags={input => search(input, selectedUnderlyingsIds, selectedUnderlyings, defaultUnderlyingCodeType)}
          getOptionLabel={(option: TagOption<Underlying>) =>
            displayUnderlyingCode(defaultUnderlyingCodeType, option.rawValue as Underlying)
          }
          isClearable={false}
          id={id ?? ''}
          isMulti={true}
          isDisabled={isDisabled}
          onTagsChanged={handleTagsChanged}
          placeholder={placeholder}
          tags={EMPTY_TAGS}
          tabIndex={tabIndex}
          btnTabIndex={-1}
          className={classnames(
            className,
            {
              disabled: isDisabled,
            },
            { 'pe-none': isCellInactive },
          )}
          onBlur={updateUnderlyings}
          isFocused={isFocused}
          onEditingKeyDown={onEditingKeyDown}
          lostInputBeforeEditing={lostInputBeforeEditing}
          onInputChanged={onInputChanged}
        />
      </WithInputGroupAppend>
      {tags.length > 0 && (
        <>
          <HorizontalDivider />
          <div className="d-flex flex-wrap">
            <ResultsComponent
              isSingleUnderlyingSelection={false}
              isDisabled={isDisabled}
              tags={validTags}
              invalidTags={invalidTags}
              onRemove={id => onUnderlyingRemovedById(id, true)}
              defaultUnderlyingCodeType={defaultUnderlyingCodeType}
              isEditing={newTags.length > 0}
              onValidTagRemovedAll={() => clearAllTags(true)}
              selectedUnderlyingsIds={selectedUnderlyingsIds}
            ></ResultsComponent>
          </div>
        </>
      )}
    </div>
  );
}

BatchMultipleUnderlyingsComponent.displayName = 'BatchMultipleUnderlyingsComponent';
